"use client";

import { useEffect, useState, type CSSProperties } from "react";
import { apiFetch } from "@/lib/api/client";
import { TAG_BY_KEY, type TagKey } from "@/lib/library-map/prototype-data";
import PhraseText from "@/components/book/PhraseText";
import { useUiTexts } from "@/lib/ui-texts-client";

interface Props {
  token: string;
  onClick: () => void;
}

/** 出やすい本の文の中で、ジャンルの札に置き換える差し込み */
const LEANING_TOKEN = "{属性}";

interface NodeSummary {
  id: string;
  name: string;
  weights: Partial<Record<TagKey, number>>;
}

/** ホームの左上: いまいる館の名前と、出やすい本の傾向（出やすさの上位2つ）。押すと場所を選ぶ画面へ。
 * 精霊に出会う確率と今日の確定は出さない（10/12 本人「確率の表示はいらない」。ガチャの「今日の条件」に出る） */
export default function CurrentLibraryPlaque({ token, onClick }: Props) {
  const [node, setNode] = useState<NodeSummary | null>(null);
  // 文は管理UIの「文言」で書き換えられる（src/lib/ui-texts.ts の home.plaque.*）
  const { t, raw } = useUiTexts();

  useEffect(() => {
    (async () => {
      const [progress, data] = await Promise.all([
        apiFetch<{ current: string }>("/api/library/progress", token),
        apiFetch<{ nodes: NodeSummary[] }>("/api/library/nodes", token),
      ]).catch(() => [null, null] as const);
      setNode(data?.nodes.find((n) => n.id === progress?.current) ?? null);
    })();
  }, [token]);

  const leaning = node
    ? (Object.entries(node.weights) as [TagKey, number][])
        .filter(([key, w]) => w > 0 && TAG_BY_KEY[key])
        .sort((a, b) => b[1] - a[1])
        .slice(0, 2)
        .map(([key]) => TAG_BY_KEY[key])
    : [];
  // 「{属性}の本が多い」の {属性} のところに、ジャンルごとの色の札を並べる（文に {属性} が無ければ、文の後ろに並べる）
  const leaningTemplate = raw("home.plaque.leaning");
  const tagAt = leaningTemplate.indexOf(LEANING_TOKEN);
  const leaningTags = leaning.map((tag) => (
    <span key={tag.key} className="library-plaque-tag" style={{ "--tag": tag.color } as CSSProperties}>
      {tag.label}
    </span>
  ));

  return (
    // 閲覧室の扉の真鍮の銘板（見た目は globals.css の .library-plaque*）
    <button
      type="button"
      onClick={onClick}
      className="library-plaque"
      // 木の地をジャンルの色で塗り分ける（globals.css の .library-plaque[data-genres]）。偏りのない館は茶色のまま
      data-genres={leaning.length > 0 ? leaning.length : undefined}
      style={leaning.length > 0 ? ({ "--genre1": leaning[0].color, "--genre2": (leaning[1] ?? leaning[0]).color } as CSSProperties) : undefined}
    >
      <span className="library-plaque-plate">
        <span className="library-plaque-eyebrow">{t("home.plaque.eyebrow")}</span>
        {/* 館の名前は文節の切れ目でだけ折る（「閲覧 / 室」のように語の途中で折らない）。文節が欄に収まらないときだけ途中で折る */}
        <span className="library-plaque-name min-w-0 break-keep [overflow-wrap:anywhere]">
          {node ? <PhraseText text={node.name} /> : "…"}
        </span>
      </span>
      {leaning.length > 0 && (
        <span className="library-plaque-info">
          {tagAt < 0 ? (
            <><span className="whitespace-nowrap opacity-75">{leaningTemplate}</span> {leaningTags}</>
          ) : (
            <>
              {/* 文は札の前と後ろでだけ折る（「多 / い」のように語の途中で折らない） */}
              {tagAt > 0 && <span className="whitespace-nowrap opacity-75">{leaningTemplate.slice(0, tagAt)}</span>}
              {leaningTags}
              <span className="whitespace-nowrap opacity-75">{leaningTemplate.slice(tagAt + LEANING_TOKEN.length)}</span>
            </>
          )}
        </span>
      )}
      <span className="library-plaque-choose">{t("home.plaque.choose")}</span>
    </button>
  );
}
